import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useCurrentRefinements, useInstantSearch } from 'react-instantsearch';
import { css } from '@emotion/core';

import { Box, Button, Icon, Text } from '../../../design-system';
import { MobileFilterBar } from './MobileFilterBar';
import { SecondDrawer } from '../../common/components/Custom';
import useScrollLock from '../../common/hooks/useScrollLock';
import { zIndex } from '../../common/constants';
import { HIERARCHICAL_ITEMS } from '../../catalog/components/HierarchicalMenu';
import { MobileSortSelector } from '../../sorting/components/MobileSortSelector';
import { useLocation } from 'react-router-dom';
import paths from '../../routing/paths';

enum DrawerContent {
  EMPTY,
  FILTERS,
  SORT_BY,
  FILTERS_SORT_BY,
}

export const MobileFilters: FC = () => {
  const { pathname } = useLocation();
  const { items } = useCurrentRefinements({ excludedAttributes: [...HIERARCHICAL_ITEMS, 'query'] });
  const { results } = useInstantSearch();
  const [drawerContent, setDrawerContent] = useState(DrawerContent.EMPTY);
  const [filtersRef, setFiltersRef] = useState<HTMLDivElement>();
  const filterListRef = useRef<HTMLDivElement | null>(null);
  useScrollLock({ ref: filtersRef, isActive: drawerContent === DrawerContent.FILTERS });
  const [sortByRef, setSortByRef] = useState<HTMLDivElement>();
  useScrollLock({ ref: sortByRef, isActive: drawerContent === DrawerContent.SORT_BY });
  const nbHits = pathname.startsWith(paths.SELECTION)
    ? (Object.values(results._rawResults[0].facets?.['categories.lvl0'] ?? {}).reduce(
        (acc, curr) => acc + curr,
        0
      ) ?? 0)
    : results.nbHits;

  const onFiltersRefChange = useCallback((node) => {
    setFiltersRef(node);
    filterListRef.current = node;
  }, []);

  const onSortByRefChange = useCallback((node) => {
    setSortByRef(node);
  }, []);

  const handleFiltersClick = () => {
    setDrawerContent(DrawerContent.FILTERS);
  };

  const handleSortByClick = () => {
    setDrawerContent(DrawerContent.SORT_BY);
  };

  const handleClose = () => {
    setDrawerContent(DrawerContent.EMPTY);
  };

  useEffect(() => {
    if (drawerContent === DrawerContent.FILTERS) {
      filterListRef.current?.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [drawerContent]);

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-around"
        position="relative"
        padding="8px 16px"
        css={css`
          gap: 16px;
        `}
      >
        <Button
          type="button"
          id="btn-filter-bar"
          onClick={handleFiltersClick}
          data-testid="mobile-filters-button-filters"
          aria-label="Bouton pour afficher les filtres"
          preset="filter_sort_by"
        >
          <Box
            display="grid"
            gridGap="s"
            gridAutoFlow="column"
            alignItems="center"
            justifyContent="center"
          >
            {items.length > 0 ? (
              <Text
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="WHITE"
                color="BLACK"
                size="l"
                preset="caption"
                borderRadius="50%"
                fontWeight="bold"
                id="filter-bar-number-of-filters-applied"
              >
                {items.length}
              </Text>
            ) : (
              <Icon name="filters" size={26} color={'WHITE'} />
            )}
            FILTRES
          </Box>
        </Button>
        <Button
          type="button"
          id="btn-sort-by-bar"
          onClick={handleSortByClick}
          data-testid="mobile-filters-button-sort-by"
          aria-label="Bouton pour afficher le tri par ordre"
          preset="filter_sort_by"
        >
          <Box
            display="grid"
            gridGap="s"
            gridAutoFlow="column"
            alignItems="center"
            justifyContent="center"
          >
            <Icon name="sortBy" size={26} color={'WHITE'} />
            TRIER PAR
          </Box>
        </Button>
      </Box>
      <SecondDrawer
        isOpen={drawerContent === DrawerContent.FILTERS}
        ref={onFiltersRefChange}
        zIndex={zIndex.SIDEBAR}
      >
        <MobileFilterBar onClose={handleClose} nbHits={nbHits} />
      </SecondDrawer>
      <SecondDrawer
        isOpen={drawerContent === DrawerContent.SORT_BY}
        ref={onSortByRefChange}
        zIndex={zIndex.SIDEBAR}
      >
        <MobileSortSelector onClose={handleClose} />
      </SecondDrawer>
    </>
  );
};
